import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const previousUserRef = useRef(null);

  // Merge local cart with server cart (combines quantities for same products)
  const mergeCarts = useCallback((localCart, serverCart) => {
    const mergedMap = new Map();
    
    // Add server cart items first
    serverCart.forEach(item => {
      mergedMap.set(item.product_id, { ...item });
    });
    
    // Merge local cart items (add quantities if product exists, otherwise add new)
    localCart.forEach(item => {
      if (mergedMap.has(item.product_id)) {
        const existing = mergedMap.get(item.product_id);
        mergedMap.set(item.product_id, {
          ...existing,
          quantity: existing.quantity + item.quantity
        });
      } else {
        mergedMap.set(item.product_id, { ...item });
      }
    });
    
    return Array.from(mergedMap.values());
  }, []);

  const fetchCart = useCallback(async () => {
    if (!user) return [];
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/cart`);
      return response.data.items || [];
    } catch (err) {
      setError('Failed to load cart');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  const saveCart = useCallback(async (items) => {
    if (user) {
      try {
        await axios.post(`${API_URL}/cart`, { user_id: user.id, items });
        setError(null);
      } catch (err) {
        setError('Failed to save cart. Changes may not persist.');
      }
    } else {
      localStorage.setItem('cart', JSON.stringify(items));
    }
  }, [user]);

  // Handle user login/logout and cart merging
  useEffect(() => {
    const handleCartOnAuthChange = async () => {
      const wasLoggedOut = previousUserRef.current === null;
      const isNowLoggedIn = user !== null;
      
      if (isNowLoggedIn) {
        // User just logged in or registered
        const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
        const serverCart = await fetchCart();
        
        if (wasLoggedOut && localCart.length > 0) {
          // Merge local cart with server cart
          const mergedCart = mergeCarts(localCart, serverCart);
          setCart(mergedCart);
          
          // Save merged cart to server
          await saveCart(mergedCart);
          
          // Clear local storage cart after merging
          localStorage.removeItem('cart');
        } else {
          // Just use server cart
          setCart(serverCart);
        }
      } else {
        // User logged out - load from localStorage
        const localCart = localStorage.getItem('cart');
        if (localCart) {
          setCart(JSON.parse(localCart));
        } else {
          setCart([]);
        }
      }
      
      previousUserRef.current = user;
    };

    handleCartOnAuthChange();
  }, [user, fetchCart, mergeCarts, saveCart]);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product_id === productId);
      let newCart;

      if (existingItem) {
        newCart = prevCart.map(item =>
          item.product_id === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newCart = [...prevCart, { product_id: productId, quantity }];
      }

      saveCart(newCart);
      return newCart;
    });
  }, [saveCart]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (quantity <= 0) {
      setCart(prevCart => {
        const newCart = prevCart.filter(item => item.product_id !== productId);
        saveCart(newCart);
        return newCart;
      });
      return;
    }

    setCart(prevCart => {
      const newCart = prevCart.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      );
      saveCart(newCart);
      return newCart;
    });
  }, [saveCart]);

  const removeFromCart = useCallback(async (productId) => {
    setCart(prevCart => {
      const newCart = prevCart.filter(item => item.product_id !== productId);
      saveCart(newCart);
      return newCart;
    });
  }, [saveCart]);

  const clearCart = useCallback(async () => {
    setCart([]);
    if (user) {
      try {
        await axios.delete(`${API_URL}/cart`);
        setError(null);
      } catch (err) {
        setError('Failed to clear cart on server');
      }
    } else {
      localStorage.removeItem('cart');
    }
  }, [user]);

  const getCartCount = useCallback(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    loading,
    error,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartCount,
    fetchCart
  }), [cart, loading, error, addToCart, updateQuantity, removeFromCart, clearCart, getCartCount, fetchCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};